'use client';

import { useState } from 'react';
import Link from 'next/link';
import { EUAIActAssessment, AssessmentResults, RiskLevel } from '@/lib/eu-ai-act-types';
import { generateAssessmentResults } from '@/lib/eu-ai-act-classifier';
import EUAIActQuestionnaire from '@/components/EUAIActQuestionnaire';
import EUAIActResults from '@/components/EUAIActResults';
import EUAIActChecklist from '@/components/EUAIActChecklist';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

type View = 'landing' | 'questionnaire' | 'results' | 'checklist';

export default function EUAIActCompliancePage() {
  const [currentView, setCurrentView] = useState<View>('landing');
  const [assessment, setAssessment] = useState<EUAIActAssessment | null>(null);
  const [results, setResults] = useState<AssessmentResults | null>(null);
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'eu-ai-act-compliance',
    toolCategory: 'compliance',
  });

  const handleQuestionnaireComplete = (completedAssessment: EUAIActAssessment) => {
    setAssessment(completedAssessment);

    const assessmentResults = generateAssessmentResults(
      completedAssessment.organization,
      completedAssessment.aiSystems,
      completedAssessment.complianceStatus
    );

    setResults(assessmentResults);
    setCurrentView('results');
  };

  const handleStartOver = () => {
    setAssessment(null);
    setResults(null);
    setCurrentView('landing');
    setSaved(false);
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!results || !assessment || !commandCenterEnabled) return;

    // Build scores from system classifications
    const scores: Record<string, number> = {
      compliance: results.complianceScore,
    };
    results.systemClassifications.forEach((c) => {
      // Convert risk level to numeric score (higher = better)
      const riskScore =
        c.riskLevel === 'minimal' ? 100 :
        c.riskLevel === 'limited' ? 75 :
        c.riskLevel === 'high' ? 50 :
        0; // unacceptable
      scores[`system_${c.system.id}`] = riskScore;
    });

    // Build gaps from compliance gaps
    const gaps = results.complianceGaps.map((gap) => ({
      id: gap.requirement.id,
      title: gap.requirement.title,
      severity: (gap.currentStatus === 'missing' ? 'critical' : 'high') as 'critical' | 'high' | 'medium' | 'low',
      description: `${gap.requirement.description} (${gap.requirement.article})`,
      recommendation: `Implement ${gap.requirement.title}. Estimated effort: ${gap.effortEstimate}`,
    }));

    // Build recommendations from prioritized actions
    const recommendations = results.prioritizedActions.map((action) => ({
      id: action.id,
      title: action.title,
      priority: (action.priority <= 2 ? 'high' : action.priority <= 4 ? 'medium' : 'low') as 'high' | 'medium' | 'low',
      description: action.description,
      effort: action.estimatedEffort,
    }));

    // Build answers from assessment data
    const answers: Record<string, unknown> = {
      organization: assessment.organization,
      aiSystems: assessment.aiSystems,
      complianceStatus: assessment.complianceStatus,
    };

    const savedAssessment = await saveResult({
      name: `EU AI Act Compliance - ${new Date().toLocaleDateString()}`,
      score_overall: results.complianceScore,
      scores,
      answers,
      gaps,
      recommendations,
      metadata: {
        systemCount: results.systemClassifications.length,
        riskLevels: results.systemClassifications.map((c) => c.riskLevel),
        nextDeadline: results.nextDeadline,
        assessmentId: assessment.id,
      },
    });

    if (savedAssessment) {
      setSaved(true);
    }
  };

  const handleExport = () => {
    if (!results || !assessment) return;

    const report = generateMarkdownReport(assessment, results);
    const blob = new Blob([report], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `eu-ai-act-assessment-${new Date().toISOString().split('T')[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Get unique risk levels for checklist
  const riskLevels: RiskLevel[] = results
    ? [...new Set(results.systemClassifications.map((c) => c.riskLevel))]
    : [];

  return (
    <div className="min-h-screen bg-white dark:bg-background py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        {currentView === 'landing' && (
          <>
            <div className="text-center mb-12">
              <div className="inline-block px-3 py-1 bg-accent-muted text-accent rounded-full text-sm font-medium mb-4">
                Regulation (EU) 2024/1689
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-light-text dark:text-foreground mb-4">
                EU AI Act Compliance Assessment
              </h1>
              <p className="text-xl text-light-text-secondary dark:text-foreground-secondary max-w-3xl mx-auto">
                Assess your AI systems against the EU AI Act requirements and get a
                personalized compliance roadmap.
              </p>
            </div>

            <LandingView onStart={() => setCurrentView('questionnaire')} />
          </>
        )}

        {currentView === 'questionnaire' && (
          <>
            <div className="mb-8">
              <button
                onClick={() => setCurrentView('landing')}
                className="text-accent hover:underline text-sm flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Overview
              </button>
            </div>
            <EUAIActQuestionnaire onComplete={handleQuestionnaireComplete} />
          </>
        )}

        {currentView === 'results' && results && (
          <EUAIActResults
            results={results}
            onViewChecklist={() => setCurrentView('checklist')}
            onExport={handleExport}
            onStartOver={handleStartOver}
            commandCenterEnabled={commandCenterEnabled}
            saved={saved}
            saving={saving}
            saveError={saveError}
            onSaveToCommandCenter={handleSaveToCommandCenter}
          />
        )}

        {currentView === 'checklist' && (
          <EUAIActChecklist
            riskLevels={riskLevels}
            onBack={() => setCurrentView('results')}
          />
        )}
      </div>
    </div>
  );
}

function LandingView({ onStart }: { onStart: () => void }) {
  return (
    <div className="space-y-12">
      {/* Value Props */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-light-surface dark:bg-surface rounded-lg p-6 text-center">
          <div className="text-4xl mb-3">🎯</div>
          <h3 className="font-semibold text-light-text dark:text-foreground mb-2">Risk Classification</h3>
          <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
            Automatically classify your AI systems into Unacceptable, High, Limited, or Minimal risk categories
          </p>
        </div>
        <div className="bg-light-surface dark:bg-surface rounded-lg p-6 text-center">
          <div className="text-4xl mb-3">📋</div>
          <h3 className="font-semibold text-light-text dark:text-foreground mb-2">Compliance Checklist</h3>
          <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
            Get a detailed checklist of requirements based on Articles 9-15 and Article 50
          </p>
        </div>
        <div className="bg-light-surface dark:bg-surface rounded-lg p-6 text-center">
          <div className="text-4xl mb-3">📅</div>
          <h3 className="font-semibold text-light-text dark:text-foreground mb-2">Timeline Tracking</h3>
          <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
            Track compliance deadlines from 2024-2027 with prioritized action plans
          </p>
        </div>
      </div>

      {/* CTA */}
      <div className="text-center">
        <button
          onClick={onStart}
          className="px-8 py-4 bg-accent text-accent-foreground rounded-lg text-lg font-bold hover:bg-accent-hover transition-colors shadow-lg"
        >
          Start Assessment
        </button>
        <p className="text-sm text-light-text-secondary dark:text-foreground-tertiary mt-3">
          Takes approximately 5-10 minutes
        </p>
      </div>

      {/* Risk Levels Overview */}
      <div className="bg-light-surface dark:bg-surface rounded-lg p-8">
        <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-6 text-center">
          EU AI Act Risk Categories
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border-l-4 border-red-500 pl-4">
            <h3 className="font-semibold text-red-400 mb-2">Unacceptable Risk (Prohibited)</h3>
            <p className="text-sm text-light-text-secondary dark:text-foreground-secondary mb-2">
              AI systems that pose unacceptable risks are banned outright.
            </p>
            <ul className="text-xs text-light-text-secondary dark:text-foreground-tertiary space-y-1">
              <li>• Social scoring by governments</li>
              <li>• Real-time biometric ID in public spaces</li>
              <li>• Emotion recognition at work/school</li>
              <li>• Manipulation techniques</li>
            </ul>
          </div>
          <div className="border-l-4 border-accent pl-4">
            <h3 className="font-semibold text-accent mb-2">High Risk</h3>
            <p className="text-sm text-light-text-secondary dark:text-foreground-secondary mb-2">
              Subject to strict requirements and conformity assessments.
            </p>
            <ul className="text-xs text-light-text-secondary dark:text-foreground-tertiary space-y-1">
              <li>• Biometric identification</li>
              <li>• Critical infrastructure</li>
              <li>• Education & employment decisions</li>
              <li>• Essential services access</li>
            </ul>
          </div>
          <div className="border-l-4 border-yellow-500 pl-4">
            <h3 className="font-semibold text-yellow-400 mb-2">Limited Risk</h3>
            <p className="text-sm text-light-text-secondary dark:text-foreground-secondary mb-2">
              Subject to transparency obligations only.
            </p>
            <ul className="text-xs text-light-text-secondary dark:text-foreground-tertiary space-y-1">
              <li>• Chatbots and virtual assistants</li>
              <li>• Emotion recognition systems</li>
              <li>• Deep fake generators</li>
              <li>• Content generation AI</li>
            </ul>
          </div>
          <div className="border-l-4 border-green-500 pl-4">
            <h3 className="font-semibold text-green-400 mb-2">Minimal Risk</h3>
            <p className="text-sm text-light-text-secondary dark:text-foreground-secondary mb-2">
              No specific requirements - voluntary compliance encouraged.
            </p>
            <ul className="text-xs text-light-text-secondary dark:text-foreground-tertiary space-y-1">
              <li>• Spam filters</li>
              <li>• AI-enabled games</li>
              <li>• Inventory management</li>
              <li>• Most business AI applications</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-light-surface dark:bg-surface rounded-lg p-8">
        <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-6 text-center">
          Key Compliance Deadlines
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
          <div className="p-4">
            <div className="text-2xl font-bold text-green-400 mb-1">Aug 2024</div>
            <div className="text-sm text-light-text dark:text-foreground font-medium">AI Act In Force</div>
            <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary">✓ Completed</div>
          </div>
          <div className="p-4">
            <div className="text-2xl font-bold text-green-400 mb-1">Feb 2025</div>
            <div className="text-sm text-light-text dark:text-foreground font-medium">Prohibitions Apply</div>
            <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary">✓ Completed</div>
          </div>
          <div className="p-4">
            <div className="text-2xl font-bold text-accent mb-1">Aug 2025</div>
            <div className="text-sm text-light-text dark:text-foreground font-medium">GPAI Rules</div>
            <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary">Upcoming</div>
          </div>
          <div className="p-4">
            <div className="text-2xl font-bold text-accent mb-1">Aug 2026</div>
            <div className="text-sm text-light-text dark:text-foreground font-medium">Full Compliance</div>
            <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary">High-risk requirements</div>
          </div>
        </div>
      </div>

      {/* Resources */}
      <div className="text-center">
        <h3 className="font-semibold text-light-text dark:text-foreground mb-4">Official Resources</h3>
        <div className="flex flex-wrap justify-center gap-4">
          <a
            href="https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A32024R1689"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-accent hover:underline"
          >
            EU AI Act Full Text →
          </a>
          <a
            href="https://digital-strategy.ec.europa.eu/en/policies/regulatory-framework-ai"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-accent hover:underline"
          >
            EU AI Strategy →
          </a>
        </div>
      </div>
    </div>
  );
}

function generateMarkdownReport(
  assessment: EUAIActAssessment,
  results: AssessmentResults
): string {
  const lines: string[] = [
    '# EU AI Act Compliance Assessment Report',
    '',
    `**Generated:** ${new Date().toLocaleDateString()}`,
    `**Assessment ID:** ${assessment.id}`,
    '',
    '---',
    '',
    '## Executive Summary',
    '',
    `- **Compliance Score:** ${results.complianceScore}%`,
    `- **AI Systems Assessed:** ${results.systemClassifications.length}`,
    `- **Compliance Gaps:** ${results.complianceGaps.length}`,
    `- **Next Deadline:** ${results.nextDeadline}`,
    '',
    '## Organization Context',
    '',
    `- **Location:** ${assessment.organization.location}`,
    `- **Size:** ${assessment.organization.size}`,
    `- **Sector:** ${assessment.organization.sector}`,
    '',
    '## AI System Classifications',
    '',
  ];

  results.systemClassifications.forEach((classification) => {
    lines.push(`### ${classification.system.name}`);
    lines.push('');
    lines.push(`- **Risk Level:** ${classification.riskLevel.toUpperCase()}`);
    lines.push(`- **Deadline:** ${classification.deadline}`);
    lines.push('');
    lines.push('**Classification Reasons:**');
    classification.reasons.forEach((reason) => {
      lines.push(`- ${reason}`);
    });
    lines.push('');
    if (classification.applicableArticles.length > 0) {
      lines.push('**Applicable Articles:**');
      classification.applicableArticles.forEach((article) => {
        lines.push(`- ${article}`);
      });
      lines.push('');
    }
  });

  if (results.complianceGaps.length > 0) {
    lines.push('## Compliance Gaps');
    lines.push('');
    results.complianceGaps.forEach((gap) => {
      lines.push(`### ${gap.requirement.title}`);
      lines.push('');
      lines.push(`- **Article:** ${gap.requirement.article}`);
      lines.push(`- **Status:** ${gap.currentStatus}`);
      lines.push(`- **Effort:** ${gap.effortEstimate}`);
      lines.push(`- **Affected Systems:** ${gap.affectedSystems.join(', ')}`);
      lines.push('');
    });
  }

  if (results.prioritizedActions.length > 0) {
    lines.push('## Prioritized Action Plan');
    lines.push('');
    results.prioritizedActions.forEach((action, i) => {
      lines.push(`### ${i + 1}. ${action.title}`);
      lines.push('');
      lines.push(action.description);
      lines.push('');
      lines.push(`- **Deadline:** ${action.deadline}`);
      lines.push(`- **Effort:** ${action.estimatedEffort}`);
      lines.push('');
    });
  }

  lines.push('---');
  lines.push('');
  lines.push('*Generated by The Art of CTO - EU AI Act Compliance Tool*');
  lines.push('');

  return lines.join('\n');
}
